/**
 * Landing Page
 * Premium enterprise landing experience
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MdDescription, MdMic, MdCardTravel, MdFlightTakeoff, MdFormatQuote, MdChevronLeft, MdChevronRight, MdPlayArrow, MdVolumeOff, MdVolumeUp, MdVideoLibrary } from 'react-icons/md';
import { FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import applicationService from '../services/applicationService';
import tutorialVideoService from '../services/tutorialVideoService';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [activeStage, setActiveStage] = useState(0);
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [feedbacks, setFeedbacks] = useState([]);
  const [tutorialVideos, setTutorialVideos] = useState([]);

  useEffect(() => {
    setIsVisible(true);
    fetchFeedbacks();
    fetchTutorialVideos();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      const response = await applicationService.getPublicFeedbacks();
      if (response.success) {
        setFeedbacks(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
    }
  };

  const fetchTutorialVideos = async () => {
    try {
      const response = await tutorialVideoService.getPublicVideos();
      if (response.success) setTutorialVideos(response.data);
    } catch {
      // silently fail — section just won't render
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Workflow stage animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Hero carousel animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHeroSlide((prev) => (prev + 1) % heroBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    navigate('/login', { state: { loginType: 'candidate' } });
  };

  const handleAdminLogin = () => {
    navigate('/login', { state: { loginType: 'hr' } });
  };

  const handleCareerClick = () => {
    navigate('/career');
  };

  const workflowStages = [
    { icon: MdDescription, label: 'Application', color: '#6B7F5C' },
    { icon: MdMic, label: 'Interview', color: '#C17A5C' },
    { icon: MdCardTravel, label: 'Visa', color: '#6B7F5C' },
    { icon: MdFlightTakeoff, label: 'Departure', color: '#C17A5C' }
  ];

  const heroBanners = [
    {
      image: '/nurse-img.jpg',
      mainText: 'Begin your HealthCare Career Abroad',
      subText: 'From Bedside to Worldwide',
      description: 'Explore international HealthCare opportunities with comprehensive support'
    },
    {
      image: '/nurse-img2.jpg',
      mainText: 'Overseas Nursing Jobs in Pioneering Institutes',
      subText: 'Sculpting Tomorrow\'s Healthcare',
      description: 'Join world-class healthcare facilities offering professional growth'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F1E8] relative overflow-x-hidden">
      <header className="sticky top-0 z-20 border-b border-white/40 bg-[#F5F1E8]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-6 py-4 sm:px-10 lg:flex-row lg:items-center lg:justify-between lg:px-24">
          <div className="flex items-center gap-3">
            <img
              src="/logo_final.png"
              alt="Jupiter International logo"
              className="h-11 w-11 object-contain"
            />
            <div>
              <p className="text-[15px] font-semibold tracking-[-0.2px] text-[#2E2E2E]">
                Jupiter International
              </p>
              <p className="text-[12px] uppercase tracking-[0.22em] text-[#7A7A7A]">
                Talent Acquisition Platform
              </p>
            </div>
          </div>

          <nav className="flex flex-wrap items-center gap-3 lg:justify-end">
            <button
              type="button"
              onClick={handleGetStarted}
              className="rounded-full bg-[#6B7F5C] px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(107,127,92,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#5d7150]"
            >
              Candidate Login
            </button>
            <button
              type="button"
              onClick={handleAdminLogin}
              className="rounded-full border border-[#2E2E2E] bg-transparent px-5 py-3 text-sm font-semibold text-[#2E2E2E] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#2E2E2E] hover:text-[#F5F1E8]"
            >
              Employee Login
            </button>
            <button
              type="button"
              onClick={handleCareerClick}
              className="rounded-full border border-[#C17A5C] bg-[#FFF7F2] px-5 py-3 text-sm font-semibold text-[#A65E40] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C17A5C] hover:text-white"
            >
              Career
            </button>
          </nav>
        </div>
      </header>

      {/* Premium Background */}
      <div className="fixed top-0 left-0 w-full h-full z-0 overflow-hidden">
        <div className="absolute rounded-full blur-[80px] opacity-15 animate-[orbFloat_25s_ease-in-out_infinite] w-[600px] h-[600px] bg-[radial-gradient(circle,#6B7F5C_0%,transparent_70%)] -top-[200px] -left-[200px]"></div>
        <div className="absolute rounded-full blur-[80px] opacity-15 animate-[orbFloat_25s_ease-in-out_infinite] w-[500px] h-[500px] bg-[radial-gradient(circle,#C17A5C_0%,transparent_70%)] -bottom-[150px] -right-[150px] [animation-delay:8s]"></div>
        <div className="absolute rounded-full blur-[80px] opacity-15 animate-[orbFloat_25s_ease-in-out_infinite] w-[400px] h-[400px] bg-[radial-gradient(circle,#2E2E2E_0%,transparent_70%)] top-[40%] right-[10%] [animation-delay:15s]"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-50 pointer-events-none" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E\")"
        }}></div>
      </div>

      {/* Healthcare Hero Carousel Section */}
      <section className="relative z-[2] w-full overflow-hidden">
        <div className="relative h-[500px] sm:h-[600px] lg:h-[700px] w-full">
          {heroBanners.map((banner, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${activeHeroSlide === index ? 'opacity-100' : 'opacity-0'
                }`}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${banner.image})`,
                  backgroundPosition: 'center'
                }}
              >
                {/* Dark Overlay for Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
              </div>

              {/* Text Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-10 lg:px-24 text-center">
                <div className={`transition-all duration-1000 ease-out transform ${activeHeroSlide === index
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
                  }`}>
                  {/* Sub Heading */}
                  <p className="text-lg sm:text-xl lg:text-2xl text-white/90 font-light tracking-wide mb-3 sm:mb-4 lg:mb-6">
                    {banner.subText}
                  </p>

                  {/* Main Heading */}
                  <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight mb-4 sm:mb-6 lg:mb-8 max-w-4xl mx-auto drop-shadow-lg">
                    {banner.mainText}
                  </h1>

                  {/* Description */}
                  <p className="text-base sm:text-lg lg:text-xl text-white/85 font-light mb-8 sm:mb-10 lg:mb-12 max-w-2xl mx-auto">
                    {banner.description}
                  </p>

                  {/* CTA Button */}
                  <button
                    onClick={handleGetStarted}
                    className="inline-block px-8 sm:px-10 py-3 sm:py-4 bg-[#6B7F5C] hover:bg-[#5d7150] text-white font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 shadow-xl"
                  >
                    Explore Opportunities
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Carousel Indicators */}
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
            {heroBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveHeroSlide(index)}
                className={`h-2 sm:h-3 rounded-full transition-all duration-300 ${activeHeroSlide === index
                  ? 'bg-white w-8 sm:w-10'
                  : 'bg-white/50 w-2 sm:w-3 hover:bg-white/75'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setActiveHeroSlide((prev) => (prev - 1 + heroBanners.length) % heroBanners.length)}
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-2 sm:p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setActiveHeroSlide((prev) => (prev + 1) % heroBanners.length)}
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-2 sm:p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
            aria-label="Next slide"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* Premium Background */}

      {/* Split Hero Section */}
      <section className={`relative z-[1] min-h-[90vh] grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-16 px-6 sm:px-10 lg:px-24 max-w-[1400px] mx-auto transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[30px]'
        }`}>
        <div className="animate-[slideInLeft_1s_cubic-bezier(0.16,1,0.3,1)_0.2s_both]">
          {/* <div className="inline-block px-4 py-2 bg-accent-green/10 text-accent-green rounded-[20px] text-[13px] font-semibold tracking-wide mb-8 border border-accent-green/20">
            Enterprise Recruitment Platform
          </div> */}
          <h1 className="text-[64px] font-extrabold text-[#2E2E2E] leading-[1.1] tracking-[-2px] mb-6">
            {/* Streamlining<br />
            Global Recruitment */}
          </h1>
          <p className="text-lg text-[#5A5A5A] leading-[1.7] mb-10 max-w-[500px]">
            End-to-end visa processing and overseas recruitment management.
            From candidate onboarding to final placement—automated, secure, and scalable.
          </p>
          <div className="flex gap-4 mb-12">
            <button
              className="relative px-8 py-4 bg-[#6B7F5C] text-white border-none rounded-xl text-base font-semibold cursor-pointer flex items-center gap-2 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-[0_4px_20px_rgba(107,127,92,0.3)] overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(107,127,92,0.4)] before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-full group"
              onClick={handleGetStarted}
            >
              Get Started
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              className="px-8 py-4 bg-transparent text-[#2E2E2E] border-2 border-[#2E2E2E] rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#2E2E2E] hover:text-[#F5F1E8] hover:-translate-y-0.5"
              onClick={handleAdminLogin}
            >
              Admin Access
            </button>
          </div>
          <div className="flex gap-12">
            <div className="text-left">
              <div className="text-4xl font-bold text-[#6B7F5C] leading-none mb-2">12</div>
              <div className="text-[13px] text-[#7A7A7A] font-medium">Workflow Stages</div>
            </div>
            <div className="text-left">
              <div className="text-4xl font-bold text-[#6B7F5C] leading-none mb-2">4</div>
              <div className="text-[13px] text-[#7A7A7A] font-medium">Role Types</div>
            </div>
            <div className="text-left">
              <div className="text-4xl font-bold text-[#6B7F5C] leading-none mb-2">100%</div>
              <div className="text-[13px] text-[#7A7A7A] font-medium">Secure</div>
            </div>
          </div>
        </div>

        <div className="animate-[slideInRight_1s_cubic-bezier(0.16,1,0.3,1)_0.4s_both]">
          <div className="relative flex flex-col gap-6 p-8">
            {workflowStages.map((stage, index) => {
              const IconComponent = stage.icon;
              return (
                <div
                  key={index}
                  className={`relative bg-white/70 backdrop-blur-[10px] border border-primary/10 rounded-2xl p-6 flex items-center gap-4 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${activeStage === index
                    ? 'bg-accent-green/15 translate-x-2.5 scale-[1.02] shadow-[0_8px_30px_rgba(107,127,92,0.2)]'
                    : index < activeStage
                      ? 'opacity-60'
                      : ''
                    }`}
                  style={{
                    borderColor: activeStage === index ? stage.color : undefined
                  }}
                >
                  <div className="text-[32px] w-[60px] h-[60px] flex items-center justify-center bg-white/80 rounded-xl flex-shrink-0">
                    <IconComponent size={32} />
                  </div>
                  <div className="text-lg font-semibold text-[#2E2E2E]">{stage.label}</div>
                  {index < workflowStages.length - 1 && (
                    <div className="absolute left-[45px] -bottom-6 w-0.5 h-6 bg-accent-green/30"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Tutorial Videos Section ── */}
      {tutorialVideos.length > 0 && (
        <section className="relative z-[1] py-20 bg-[#F5F1E8]">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-16">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#6B7F5C]/30 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#6B7F5C] backdrop-blur mb-5">
                <MdVideoLibrary size={16} />
                Tutorial Videos
              </div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-[-1px] text-[#2E2E2E]">
                Learn How It Works
              </h2>
              <p className="mt-4 text-lg text-[#5A5A5A] max-w-xl mx-auto">
                Step-by-step guides to help you navigate the recruitment process with confidence.
              </p>
            </div>
            <TutorialVideoGrid videos={tutorialVideos} />
          </div>
        </section>
      )}

      {/* ── Success Stories Section ── */}
      <section className="relative z-[1] py-20 bg-gradient-to-b from-[#F5F1E8] to-[#1a1a1a]">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-16">

          {/* Section Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#6B7F5C]/30 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#6B7F5C] backdrop-blur mb-5">
              <MdFormatQuote size={16} />
              Success Stories
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-[-1px] text-[#2E2E2E]">
              Hear From Our Candidates
            </h2>
            <p className="mt-4 text-lg text-[#5A5A5A] max-w-xl mx-auto">
              Real journeys. Real voices. Watch and read what our placed candidates say.
            </p>
          </div>

          {/* Video Carousel */}
          <VideoCarousel feedbacks={feedbacks.filter(f => f.videoUrl)} />

          {/* Text Feedbacks */}
          <TextFeedbackCarousel feedbacks={feedbacks.filter(f => f.feedback)} />
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-[1] border-t border-[#E4E4E4] bg-white text-[#5F5F5F]">
        <div className="mx-auto max-w-[1600px] px-6 py-10 sm:px-10 lg:px-16 xl:px-24">


          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="text-left">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#2E2E2E]">LICENCED UNDER</p>
              <div className="mt-3 flex items-start gap-4 border-t border-[#E6E6E6] pt-3">
                <img
                  src="/image.png"
                  alt="Government emblem"
                  className="h-28 w-28 object-contain"
                />
                <div>
                  <p className="text-xs font-semibold tracking-wide text-[#5F5F5F]">MINISTRY OF</p>
                  <p className="mt-1 text-xl font-bold leading-7 text-[#2E2E2E]">MICRO, SMALL &amp; MEDIUM ENTERPRISES</p>
                  <p className="mt-3 text-sm text-[#626262]">REG No: <span className="font-semibold text-[#2E2E2E]">UDYAM-TN-02-0204349</span></p>
                </div>
              </div>
            </div>

            <div className="text-left lg:text-right">
              <img
                src="/image.png"
                alt="Ministry of External Affairs emblem"
                className="h-20 w-20 mr-40 object-contain lg:ml-auto"
              />
              <p className="mt-3 text-lg leading-7 text-[#5F5F5F]">APPROVED BY MINISTRY OF EXTERNAL AFFAIRS,<br />GOVERNMENT OF INDIA</p>
              <p className="mt-2 text-sm text-[#626262]">REG NO : <span className="font-semibold text-[#2E2E2E]">CHENNAI/PROPRIETORSHIP/5533681/2019</span></p>
              <div className="mt-5 flex items-center gap-5 text-[#666666] lg:justify-end">
                <a href="https://www.instagram.com/jupiterinternational.chennai?igsh=bGFzaHRoamF6dnp5" aria-label="Instagram" className="transition-transform hover:scale-110" target='_blank'>
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg" alt="Instagram" className="h-[22px] w-[22px] object-contain" />
                </a>
                <a href="https://www.facebook.com/share/1AoQVDBtws/?mibextid=wwXIfr" aria-label="Facebook" className="transition-transform hover:scale-110" target='_blank'>
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg" alt="Facebook" className="h-[22px] w-[22px] object-contain" />
                </a>
                <a href="https://youtube.com/@haritocool?si=l7EQ1aiyQFFbhoc7" aria-label="YouTube" className="transition-transform hover:scale-110" target='_blank'>
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg" alt="YouTube" className="h-[22px] w-auto object-contain" />
                </a>
                {/* <a href="https://www.linkedin.com/company/jupiter-international" aria-label="LinkedIn" className="transition hover:text-[#2E2E2E]" target='_blank'><FaLinkedinIn size={18} /></a> */}
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#E4E4E4] px-6 py-5 text-left text-sm text-[#6D6D6D] sm:px-10 lg:px-16 xl:px-24">
          <p>Copyrights @ 2023 . All rights Reserved to Jupiter International</p>
        </div>
      </footer>
    </div>
  );
};

// ─── TutorialVideoCard ────────────────────────────────────────────────────────
const TutorialVideoCard = ({ video }) => {
  const [open, setOpen] = useState(false);

  const videoId = (() => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];
    for (const p of patterns) {
      const m = video.youtubeUrl.match(p);
      if (m) return m[1];
    }
    return null;
  })();

  if (!videoId) return null;

  const thumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  return (
    <>
      <div
        className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 hover:shadow-[0_8px_32px_rgba(107,127,92,0.15)] hover:-translate-y-1 transition-all duration-300"
        onClick={() => setOpen(true)}
      >
        <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
          <img src={thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-[#2E2E2E] text-sm leading-snug">{video.title}</h3>
          {video.description && (
            <p className="text-xs text-[#7A7A7A] mt-1 line-clamp-2">{video.description}</p>
          )}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl"
            style={{ aspectRatio: '16/9' }}
            onClick={e => e.stopPropagation()}
          >
            <iframe
              src={embedUrl}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors z-10"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── TutorialVideoGrid ────────────────────────────────────────────────────────
const TutorialVideoGrid = ({ videos }) => {
  if (!videos || videos.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {videos.map((v) => (
        <TutorialVideoCard key={v._id} video={v} />
      ))}
    </div>
  );
};

// ─── VideoCard ────────────────────────────────────────────────────────────────
// TikTok-style vertical card: full-cover video that plays only when in viewport
const VideoCard = ({ feedback }) => {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [showPlay, setShowPlay] = useState(false);

  // Intersection Observer – play when ≥60% visible, pause otherwise
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          videoRef.current.play().then(() => setPlaying(true)).catch(() => { });
        } else {
          videoRef.current.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) videoRef.current.muted = !videoRef.current.muted;
    setMuted(m => !m);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative flex-shrink-0 w-[280px] sm:w-[320px] rounded-2xl overflow-hidden cursor-pointer group"
      style={{ aspectRatio: '9/16' }}
      onClick={togglePlay}
      onMouseEnter={() => setShowPlay(true)}
      onMouseLeave={() => setShowPlay(false)}
    >
      <video
        ref={videoRef}
        src={feedback.videoUrl}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />

      {/* Play / Pause hint */}
      {(showPlay || !playing) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <MdPlayArrow size={28} className="text-white" style={{ display: playing ? 'none' : 'block' }} />
          </div>
        </div>
      )}

      {/* Mute toggle */}
      <button
        onClick={toggleMute}
        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors z-10"
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? <MdVolumeOff size={16} /> : <MdVolumeUp size={16} />}
      </button>

      {/* Candidate info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
        <p className="text-white font-semibold text-sm leading-tight">{feedback.candidateName}</p>
        {feedback.feedback && (
          <p className="text-white/70 text-xs mt-1 line-clamp-2">{feedback.feedback}</p>
        )}
      </div>
    </div>
  );
};

// ─── VideoCarousel ────────────────────────────────────────────────────────────
// Horizontal scrolling row with left/right arrow buttons
const VideoCarousel = ({ feedbacks }) => {
  const trackRef = useRef(null);
  const SCROLL_BY = 260;

  const scroll = useCallback((dir) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: dir * SCROLL_BY, behavior: 'smooth' });
    }
  }, []);

  if (!feedbacks || feedbacks.length === 0) return null;

  return (
    <div className="relative group/carousel">
      {/* Left Arrow */}
      <button
        onClick={() => scroll(-1)}
        aria-label="Scroll left"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2E2E2E] hover:bg-[#6B7F5C] hover:text-white transition-all duration-200 opacity-0 group-hover/carousel:opacity-100"
      >
        <MdChevronLeft size={24} />
      </button>

      {/* Scrollable track */}
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto pb-3 scroll-smooth no-scrollbar"
      >
        {feedbacks.map((fb, i) => (
          <VideoCard key={i} feedback={fb} />
        ))}
      </div>

      {/* Right Arrow */}
      <button
        onClick={() => scroll(1)}
        aria-label="Scroll right"
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2E2E2E] hover:bg-[#6B7F5C] hover:text-white transition-all duration-200 opacity-0 group-hover/carousel:opacity-100"
      >
        <MdChevronRight size={24} />
      </button>
    </div>
  );
};

// ─── TextFeedbackCard ────────────────────────────────────────────────────────
const TextFeedbackCard = ({ feedback }) => {
  const avatarUrl = feedback.candidatePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(feedback.candidateName)}&background=6B7F5C&color=fff&rounded=true&bold=true`;

  return (
    <div className="flex-shrink-0 w-[280px] sm:w-[350px] bg-white rounded-2xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-gray-100 flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <img 
          src={avatarUrl} 
          alt={feedback.candidateName} 
          className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-gray-100"
        />
        <div>
          <h4 className="font-semibold text-[#2E2E2E] text-base leading-tight">{feedback.candidateName}</h4>
          <p className="text-xs text-[#7A7A7A] mt-0.5">{feedback.country || 'Candidate'}</p>
        </div>
      </div>
      <p className="text-[#5A5A5A] text-sm leading-relaxed">
        "{feedback.feedback}"
      </p>
    </div>
  );
};

// ─── TextFeedbackCarousel ────────────────────────────────────────────────────
const TextFeedbackCarousel = ({ feedbacks }) => {
  const trackRef = useRef(null);
  const SCROLL_BY = 300;

  const scroll = useCallback((dir) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: dir * SCROLL_BY, behavior: 'smooth' });
    }
  }, []);

  if (!feedbacks || feedbacks.length === 0) return null;

  return (
    <div className="mt-16">
      <div className="text-center mb-8">
        <p className="text-[#5A5A5A] text-sm sm:text-base">Real experiences from candidates who completed their journey with us</p>
      </div>

      <div className="relative group/textcarousel">
        {/* Left Arrow */}
        <button
          onClick={() => scroll(-1)}
          aria-label="Scroll left"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2E2E2E] hover:bg-[#6B7F5C] hover:text-white transition-all duration-200 opacity-0 group-hover/textcarousel:opacity-100"
        >
          <MdChevronLeft size={24} />
        </button>

        {/* Scrollable track */}
        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto pb-4 pt-2 scroll-smooth no-scrollbar px-4"
        >
          {feedbacks.map((fb, i) => (
            <TextFeedbackCard key={i} feedback={fb} />
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll(1)}
          aria-label="Scroll right"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2E2E2E] hover:bg-[#6B7F5C] hover:text-white transition-all duration-200 opacity-0 group-hover/textcarousel:opacity-100"
        >
          <MdChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};

export default LandingPage;
